import {
  KnownSettingsNodeDtoStyle,
  KnownSettingsNodeDtoType,
  SettingsNodeDto
} from '@groundfloor-platform/mgmt-service-g-sdk';

// Identifies which API slice a node was loaded from. Carried on every node and action so the reducer
// knows whether to refetch the root projects list or a specific project's lazy-loaded children after a mutation.
export enum SettingsTreeDataRequestType {
  Projects = 'projects',
  ProjectChildren = 'project-children'
}

// Represents the type of relationship between two nodes in the tree
export enum RelationshipType {
  AccessedVia = 'AccessedVia',
  Unknown = 'Unknown'
}

export type SettingsNodeStyle = KnownSettingsNodeDtoStyle;

const knownSettingsNodeStyles: readonly KnownSettingsNodeDtoStyle[] = Object.values(KnownSettingsNodeDtoStyle);

export function normalizeSettingsNodeStyle(style?: string): SettingsNodeStyle | undefined {
  if (!style) {
    return undefined;
  }

  return knownSettingsNodeStyles.includes(style as SettingsNodeStyle) ? (style as SettingsNodeStyle) : undefined;
}

export interface EnrichedSettingsNodeDto extends SettingsNodeDto {
  isPlaceholder?: boolean;
  // The backend already emits style for tree presentation, but the current SDK type does not include it yet.
  style?: SettingsNodeStyle;
  request: {
    type: SettingsTreeDataRequestType;
    rootId: string;
  };
}

export interface EnrichedSettingsTreeDto {
  nodes: EnrichedSettingsNodeDto[];
}

// The reducer keeps track of the state of the settings tree and the editor panel.
// It holds the selected node and the form data for each node.
// This allows the user to navigate the settings tree without losing the form data.
export type SettingsFormState = {
  nodeId: string;
  parentNodeId?: string;
  requestType: SettingsTreeDataRequestType;
  parentProjectId?: string;
  parentEntityId?: string;
  parentNodeType?: KnownSettingsNodeDtoType;
  nodeType: KnownSettingsNodeDtoType;
  formType: SettingsFormType;
  data?: Record<string, any>;
  form: Record<string, any>;
  readOnly?: boolean;
  isLoading: boolean;
  isRegistered: boolean;
  isTouched: boolean;
  isSubmitting: boolean;
  isSuccess: boolean;
  error?: string;
};

export type SettingsFormType = 'create' | 'edit';

export type SettingsState = {
  selectedNode?: EnrichedSettingsNodeDto & { parentId?: string };
  // The enqueuedNodeSelection is used to select a node once it becomes available in the (lazily
  // loaded) tree. This is used both after a node is created (it isn't immediately in the tree) and
  // for deep-linking into the tree from elsewhere in the app.
  // The node is disambiguated by either:
  //  - parentNodeId: the tree-node id of the expected parent (used by the post-create flow, where we
  //    know the parent node), or
  //  - rootId: the managing project the occurrence belongs to (request.rootId). Used by deep-links,
  //    where the caller knows the managing project but not the internal tree-node id.
  enqueuedNodeSelection?: {
    entityId: string;
    parentNodeId?: string;
    rootId?: string;
    requestType: SettingsTreeDataRequestType;
    // When true, expand the node itself (if expandable) once selected — used by deep-links.
    expandNode?: boolean;
  };
  readOnly?: boolean;
  // Setting the Type as Partial ensures that Typescript enforces that a value might be undefined when accessed using square brackets notation.
  forms: Partial<Record<string, SettingsFormState>>;
  // The reducer holds the Tree data in state.
  // This is the single source of truth for the settings tree.
  // We store it in the DTO format returned by the API, because it is easier to work with.
  // The reducer might manipulate the tree data to do things like insert placeholder nodes for new nodes.
  projectTree: EnrichedSettingsTreeDto;
  projectChildrenTree: EnrichedSettingsTreeDto;
  placeholderNodes: EnrichedSettingsNodeDto[];
  // The reducer controls which nodes on the tree are expanded. This allows the recucer to perform
  // actions such as creating a new node and expanding the parent node to show the new node.
  expandedNodeIds: string[];
  // The reducer keeps track of the last expanded node. This allows the reducer to listen to changes and know
  // if a node was expanded or collapsed, and if it was expanded, which node it was.
  lastExpandedNodeId?: string;
  // The reducer keeps track of the current action being performed on the tree so the SettingsPageObserver
  // can render the necessary modals. Currently we only have one type of action, but later we can add
  // more actions and improve the typing here.
  currentAction?:
    | {
        type: 'DELETE_NODE';
        nodeId: string;
        entityId: string;
        parentNodeId?: string;
        nodeType: KnownSettingsNodeDtoType;
        requestType: SettingsTreeDataRequestType;
        childNodeIds: string[];
        projectId?: string;
        label?: string;
      }
    // This is different to delete node.
    // This removes the relationship between two nodes in the tree.
    // The related node is not necessarily directly a parent to the selected node i.e. nested in a folder node
    | {
        type: 'DELETE_RELATIONSHIP';
        relatedEntityId?: string;
        selectedEntityId: string;
        relationshipType: RelationshipType;
      }
    | {
        type: 'DELETE_NODE_SUCCESS';
      }
    | {
        type: 'OPEN_REGISTRATION_PAGE';
        registrationPageId: string;
        projectId: string;
      }
    | {
        type: 'HUB_CREATED';
        hubId: string;
        hubName: string;
        managedByProjectId: string;
      }
    | {
        type: 'PROJECT_CREATED';
      }
    | {
        type: 'ADD_CARRIER';
        parentProjectId: string;
        parentNodeId: string;
      }
    | {
        type: 'CARRIER_PROJECT_CREATED';
        name: string;
      }
    | {
        type: 'ASSIGN_NETWORKS';
        organisationGroupId: string;
        carrierName?: string;
      }
    | {
        type: 'ADD_REGION';
        carrierProjectId: string;
        carrierProjectName?: string;
        parentNodeId: string;
      }
    | {
        type: 'APPLY_KIOSK_THEME_PRESET';
        nodeId: string;
        kioskId: string;
        projectId: string;
      };
};
